// src/pages/Chat.jsx
import { useEffect, useRef, useState, useCallback } from 'react';
import { useLang } from '../lib/i18n.jsx';
import QuickExit from '../components/QuickExit.jsx';
import {
  startCase, sendMessage, pollForStaffMessages, saveResumePin, resumeCase, notifyTyping,
} from '../lib/chatApi.js';

// sessionStorage, not localStorage — cleared when the tab/browser closes.
// For someone using a shared or borrowed device, that matters more than
// convenience; resume-by-PIN (case ref + PIN) is the deliberate, opt-in
// way back in, not silent persistence.
function useCaseSession() {
  const [caseId, setCaseId] = useState(() => sessionStorage.getItem('sauti_case_id'));
  const [caseRef, setCaseRef] = useState(() => sessionStorage.getItem('sauti_case_ref'));
  const set = (id, ref) => {
    sessionStorage.setItem('sauti_case_id', id);
    sessionStorage.setItem('sauti_case_ref', ref);
    setCaseId(id); setCaseRef(ref);
  };
  return { caseId, caseRef, set };
}

export default function Chat() {
  const { t, lang } = useLang();
  const session = useCaseSession();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [humanMode, setHumanMode] = useState(false);
  const [staffName, setStaffName] = useState(null);
  const [showResume, setShowResume] = useState(false);
  const [showSavePin, setShowSavePin] = useState(false);
  const bottomRef = useRef(null);
  const typingTimeout = useRef(null);
  const lastPolledId = useRef(0);

  // --- Bootstrap: start a new case if we don't have one for this session ---
  useEffect(() => {
    if (session.caseId) return;
    startCase(lang).then(({ caseId, caseRef }) => session.set(caseId, caseRef)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Poll for staff messages while a human owns the conversation ---
  useEffect(() => {
    if (!session.caseId) return;
    const iv = setInterval(async () => {
      const body = await pollForStaffMessages(session.caseId, lastPolledId.current);
      if (!body) return;
      setHumanMode(body.humanMode);
      setStaffName(body.staffName);
      if (body.messages?.length) {
        setMessages((prev) => [...prev, ...body.messages.map((m) => ({ role: 'staff', content: m.content, id: m.id }))]);
        lastPolledId.current = Math.max(lastPolledId.current, ...body.messages.map((m) => m.id));
      }
    }, 4000);
    return () => clearInterval(iv);
  }, [session.caseId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = useCallback(async (e) => {
    e.preventDefault();
    if (!input.trim() || !session.caseId || sending) return;
    const userMsg = { role: 'user', content: input.trim() };
    const history = [...messages, userMsg].map(({ role, content }) => ({ role, content }));
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setSending(true);

    let assistantText = '';
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    await sendMessage(session.caseId, history, {
      onToken: (text) => {
        assistantText += text;
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: 'assistant', content: assistantText };
          return copy;
        });
      },
      onHumanAck: (name) => {
        setHumanMode(true);
        setStaffName(name);
        setMessages((prev) => prev.slice(0, -1)); // remove the empty assistant placeholder
      },
      onError: () => {
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: 'assistant', content: "I'm having trouble connecting. Please try again in a moment." };
          return copy;
        });
      },
    });

    setSending(false);
    if (messages.length >= 4 && !showSavePin) setShowSavePin(true);
  }, [input, messages, session.caseId, sending, showSavePin]);

  function handleTyping(e) {
    setInput(e.target.value);
    if (typingTimeout.current) return;
    notifyTyping(session.caseId);
    typingTimeout.current = setTimeout(() => { typingTimeout.current = null; }, 3000);
  }

  return (
    <div className="min-h-screen flex flex-col bg-cream">
      <QuickExit />
      <header className="flex items-center justify-between px-4 py-3 border-b border-ink/10 bg-white">
        <div className="font-display font-semibold text-sage-600">Sauti</div>
        {session.caseRef && (
          <button onClick={() => setShowResume(true)} className="text-xs text-ink/50 hover:text-ink">
            {session.caseRef}
          </button>
        )}
      </header>

      {humanMode && (
        <div className="bg-sage-100 text-sage-600 text-xs text-center py-1.5">
          You're now talking with {staffName ?? 'a member of our team'}.
        </div>
      )}

      <main className="flex-1 overflow-y-auto px-4 py-4 space-y-3 max-w-lg mx-auto w-full">
        {messages.length === 0 && (
          <p className="text-center text-sm text-ink/50 mt-8">You can start typing whenever you're ready.</p>
        )}
        {messages.map((m, i) => (
          <div key={m.id ?? i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
              m.role === 'user' ? 'bg-sage text-white' : 'bg-white border border-ink/10 text-ink'
            }`}>
              {m.content || (i === messages.length - 1 && sending ? '…' : '')}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </main>

      <form onSubmit={handleSend} className="px-4 py-3 border-t border-ink/10 bg-white flex gap-2 max-w-lg mx-auto w-full">
        <input
          value={input} onChange={handleTyping}
          placeholder={t.chatPlaceholder}
          className="flex-1 rounded-full border border-ink/20 px-4 py-2.5 text-sm focus:border-sage focus:ring-1 focus:ring-sage"
        />
        <button
          type="submit" disabled={sending || !input.trim()}
          className="rounded-full bg-sage hover:bg-sage-600 text-white text-sm font-medium px-5 py-2.5 disabled:opacity-50 transition-colors"
        >
          {t.send}
        </button>
      </form>

      {showSavePin && (
        <SavePinPrompt caseId={session.caseId} onDone={() => setShowSavePin(false)} t={t} />
      )}
      {showResume && (
        <ResumeModal
          onResumed={(id, ref, msgs) => { session.set(id, ref); setMessages(msgs); setShowResume(false); }}
          onClose={() => setShowResume(false)}
          t={t}
        />
      )}
    </div>
  );
}

function SavePinPrompt({ caseId, onDone, t }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  async function submit(e) {
    e.preventDefault();
    try {
      await saveResumePin(caseId, pin);
      onDone();
    } catch (err) {
      setError(err.message);
    }
  }
  return (
    <div className="fixed inset-0 bg-ink/40 flex items-end sm:items-center justify-center p-4 z-40">
      <form onSubmit={submit} className="bg-white rounded-xl p-5 max-w-xs w-full space-y-3">
        <p className="text-sm font-medium">{t.savePinPrompt}</p>
        <input
          value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
          inputMode="numeric" placeholder="••••"
          className="w-full text-center tracking-[0.5em] rounded-md border border-ink/20 px-3 py-2 font-mono"
        />
        {error && <p className="text-xs text-coral-600">{error}</p>}
        <div className="flex gap-2">
          <button type="button" onClick={onDone} className="flex-1 text-xs text-ink/50 py-2">Skip</button>
          <button type="submit" className="flex-1 rounded-md bg-sage text-white text-xs font-medium py-2">{t.savePinButton}</button>
        </div>
      </form>
    </div>
  );
}

function ResumeModal({ onResumed, onClose, t }) {
  const [caseRef, setCaseRef] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  async function submit(e) {
    e.preventDefault();
    try {
      const body = await resumeCase(caseRef, pin);
      onResumed(body.caseId, caseRef.toUpperCase(), body.messages.map((m) => ({ role: m.role, content: m.content })));
    } catch (err) {
      setError(err.message);
    }
  }
  return (
    <div className="fixed inset-0 bg-ink/40 flex items-end sm:items-center justify-center p-4 z-40">
      <form onSubmit={submit} className="bg-white rounded-xl p-5 max-w-xs w-full space-y-3">
        <p className="text-sm font-medium">{t.resumeTitle}</p>
        <input value={caseRef} onChange={(e) => setCaseRef(e.target.value)} placeholder="SAU-2026-0001" className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm font-mono" />
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} inputMode="numeric" placeholder="PIN" className="w-full text-center tracking-[0.5em] rounded-md border border-ink/20 px-3 py-2 font-mono" />
        {error && <p className="text-xs text-coral-600">{error}</p>}
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 text-xs text-ink/50 py-2">Cancel</button>
          <button type="submit" className="flex-1 rounded-md bg-sage text-white text-xs font-medium py-2">{t.resumeButton}</button>
        </div>
      </form>
    </div>
  );
}
